import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {Badge} from 'antd';
import {Link, navigate} from 'gatsby';
import useOrderDisplayState from './use-order-display-state';
import useLogistic from './use-logistic-data';
import {
  ORDER_TYPE,
  DELIVERY_TYPE,
  PAYMENT_STATUS,
  PAYMENT_SUBTYPE,
  PERIOD_STATUS,
} from '../dictionary';
import ORDER_TABLE_TYPES from '../Components/OrderTable/orderTableTypes';
import {Tag, Text} from '../Widgets';
import {dateUtil} from '../Utils';
import {Context} from '../AppContext';
const appConfig = require('../data.json');
const qs = require('query-string');

const PAYMENT_STATUS_COLOR = {
  waiting: {textColor: '#9b9b9b', color: '#e6e6e6'},
  processing: {textColor: '#9b9b9b', color: '#e6e6e6'},
  code_generated: {textColor: '#9b9b9b', color: '#e6e6e6'},
  success: {textColor: '#fff', color: appConfig.colors.main},
  failure: {textColor: '#c89664', color: '#fff7d0'},
};

const ORDER_STATE_COLOR = {
  voided: appConfig.colors.textThird,
  review_rejected: appConfig.colors.red,
};

function OrderStateCell({order}) {
  const orderState = useOrderDisplayState({
    display_state: order.display_state,
    payment_type: order.payment_type,
    payment_subtype: order.payment_subtype,
    order_type: order.order_type,
    logistics: order.logistics,
    payment_status: order.payment_status,
  });
  return (
    <Badge dot={order.is_item_rejected}>
      <span
        style={{
          color:
            ORDER_STATE_COLOR[order.display_state] || appConfig.colors.text,
        }}>
        {orderState.title}
      </span>
    </Badge>
  );
}

function LogisticCell({order}) {
  let {logistics} = useLogistic({order, logistics: order.logistics});

  if (!logistics || logistics.length === 0) {
    return '---';
  }

  return DELIVERY_TYPE[logistics[0].logistics_type] || '---';
}

export default function useOrderTable({
  pathname,
  query,
  defaultType,
  defaultMonthly,
}) {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState([]);
  const app = useContext(Context);

  const filters = useMemo(() => {
    return {
      search: query.search || '',
      pageSize: parseInt(query.pageSize) || 20,
      current: parseInt(query.current) || 1,
      type: query.type || defaultType || '',
      monthly_order: defaultMonthly,
    };
  }, [query.search, query.pageSize, query.current, query.type]);

  const changeFilters = useCallback(
    (obj, resetPaging = false) => {
      const params = {
        ...filters,
        ...obj,
        ...(resetPaging && {current: 1, pageSize: 20}),
      };

      const paramsStr = qs.stringify(params);
      navigate(`${pathname}?${paramsStr}`);
    },
    [pathname, filters],
  );

  const getRecords = useCallback(async () => {
    if (!filters.type || !app.state.profile) {
      return; // initial state
    }
    app.actions.setLoading(true);
    let params = {
      payment_status: '',
      payment_type: '',
      search: filters.search,
      limit: filters.pageSize,
      offset: (filters.current - 1) * filters.pageSize,
    };

    // let payment_status =
    //   {
    //     [ORDER_TABLE_TYPES.SUCCESS]: `success`,
    //     [ORDER_TABLE_TYPES.WAITING_OR_PROCESSING]: `waiting,processing,code_generated`,
    //   }[filters.type] || '';

    // let payment_type =
    //   {
    //     [ORDER_TABLE_TYPES.MONTHLY]: `monthly`,
    //     [ORDER_TABLE_TYPES.CURRENT_MONTHLY]: 'monthly',
    //     // [ORDER_TABLE_TYPES.CREDIT_PAY]: 'credits',
    //   }[filters.type] || '';

    try {
      switch (filters.type) {
        case ORDER_TABLE_TYPES.WAITING_OR_PROCESSING:
          params.payment_status = `waiting,processing,code_generated`;
          break;
        case ORDER_TABLE_TYPES.ORDER_ITEM_REJECTED:
          // params.is_item_rejected = true;
          params.display_state = 'review_rejected';
          break;
        case ORDER_TABLE_TYPES.SUCCESS:
          params.payment_status = `success`;
          break;
        case ORDER_TABLE_TYPES.CREDIT_ORDER:
          params.order_type = 'credit';
          break;
        case ORDER_TABLE_TYPES.MONTHLY:
          params.payment_type = 'monthly';
          params.monthly_order = filters.monthly_order;
          params.no_page = true;
          break;
        case ORDER_TABLE_TYPES.CURRENT_MONTHLY:
          params.payment_type = 'monthly';
          params.monthly_unbilled = true;
          break;
        case ORDER_TABLE_TYPES.PERIOD:
          params.order_type = 'period';
          params.no_page = true;
          break;
        case ORDER_TABLE_TYPES.NORMAL:
        default:
          break;
      }

      let resp = await app.actions.getOrders(params);

      // if (filters.type === ORDER_TABLE_TYPES.ORDER_ITEM_REJECTED) {
      //   orders = orders.filter((order) => order.is_item_rejected);
      // }

      // if (filters.type === ORDER_TABLE_TYPES.CREDIT_ORDER) {
      //   orders = orders.filter((order) => order.order_type === 'credit');
      // }
      if (
        filters.type === ORDER_TABLE_TYPES.MONTHLY ||
        filters.type === ORDER_TABLE_TYPES.PERIOD
      ) {
        setRecords(resp);
      } else {
        let {results: orders, count} = resp;
        setRecords(orders);
        setTotal(count);
      }
    } catch (ex) {
      console.warn(ex);
    }
    app.actions.setLoading(false);
  }, [app.actions, filters, app.state.profile]);

  useEffect(() => {
    getRecords();
  }, [getRecords]);

  const columns = useMemo(() => {
    let columns = [];
    switch (filters?.type) {
      //此table 都是 order_type  = credit
      case ORDER_TABLE_TYPES.CREDIT_ORDER:
        columns = [
          {
            title: '訂單編號',
            render: (instance) => (
              <Link to={`/order?id=${instance.id}`}>
                <Text size="sm" color={appConfig.colors.main}>
                  {instance.display_id || `-`}
                </Text>
              </Link>
            ),
            width: 150,
            fixed: 'left',
          },
          {
            title: '付款方式',
            render: (order) => PAYMENT_SUBTYPE[order.payment_subtype],
            width: 100,
          },
          {
            title: '付款狀態',
            render: (order) => {
              let colorSet = PAYMENT_STATUS_COLOR[order.payment_status] || {};
              return (
                <Tag
                  color={colorSet.color}
                  textColor={colorSet.textColor}
                  style={{borderRadius: 6}}
                  text={PAYMENT_STATUS[order.payment_status]}
                />
              );
            },
            width: 70,
          },
          {
            title: '加值金額',
            dataIndex: 'amount',
            width: 100,
          },
          {
            title: '加值點數',
            dataIndex: 'amount',
            width: 100,
          },
          {
            title: '建立時間',
            render: (order) =>
              dateUtil.format(order.created, 'YYYY-MM-DD HH:mm'),
            width: 130,
          },
        ];
        break;

      //此table 都是 payment_type  = credits
      // case ORDER_TABLE_TYPES.CREDIT_PAY:
      //   columns = [
      //     {
      //       title: '訂單編號',
      //       render: (instance) => (
      //         <Link to={`/order?id=${instance.id}`}>
      //           <Text size="sm" color={appConfig.colors.main}>
      //             {instance.display_id || `-`}
      //           </Text>
      //         </Link>
      //       ),
      //       width: 150,
      //       fixed: 'left',
      //     },
      //     {
      //       title: '訂單總額',
      //       dataIndex: 'amount',
      //       width: 100,
      //     },
      //     {
      //       title: '使用點數',
      //       dataIndex: 'amount',
      //       width: 100,
      //     },
      //     {
      //       title: '建立時間',
      //       render: (order) => dateUtil.format(order.created, 'YYYY-MM-DD HH:mm'),
      //       width: 130,
      //     },
      //   ];
      //   break;

      case ORDER_TABLE_TYPES.MONTHLY:
      case ORDER_TABLE_TYPES.CURRENT_MONTHLY:
        columns = [
          {
            title: '訂單編號',
            render: (instance) => (
              <Link to={`/order?id=${instance.id}`}>
                <Text size="sm" color={appConfig.colors.main}>
                  {instance.display_id || `-`}
                </Text>
              </Link>
            ),
            width: 70,
            fixed: 'left',
            expandable: true,
          },
          {
            title: '訂單狀態',
            render: (order) => <OrderStateCell order={order} />,
            width: 100,
          },
          {
            title: '付款狀態',
            render: (order) => {
              let text = order.monthly_order ? '已出帳' : '未出帳';
              let colorSet = order.monthly_order
                ? {textColor: '#fff', color: appConfig.colors.main}
                : {textColor: '#9b9b9b', color: '#e6e6e6'};

              // if (
              //   order.payment_type === 'monthly' &&
              //   order.payment_status === 'success'
              // ) {
              //   text = ;
              // }
              return (
                <Tag
                  color={colorSet.color}
                  textColor={colorSet.textColor}
                  style={{borderRadius: 6, maxWidth: 100}}
                  text={text}
                />
              );
            },
            width: 70,
          },
          {
            title: '運送方式',
            render: (order) => <LogisticCell order={order} />,
            width: 100,
          },
          {
            title: '總價',
            render: (order) => (
              <span style={{color: appConfig.colors.main}}>{order.amount}</span>
            ),
            width: 70,
          },
          {
            title: '訂單類型',
            render: (order) => {
              return ORDER_TYPE[order.order_type];
            },
            width: 70,
          },
        ];
        break;

      case ORDER_TABLE_TYPES.PERIOD:
        columns = [
          {
            title: '訂單編號',
            render: (instance) => (
              <Link to={`/order?id=${instance.id}`}>
                <Text size="sm" color={appConfig.colors.main}>
                  {instance.display_id || `-`}
                </Text>
              </Link>
            ),
            width: 100,
            fixed: 'left',
          },
          {
            title: '訂單狀態',
            render: (order) => <OrderStateCell order={order} />,
            width: 100,
          },
          {
            title: '付款狀態',
            render: (order) => {
              let colorSet = PAYMENT_STATUS_COLOR[order.payment_status] || {};
              let text = PAYMENT_STATUS[order.payment_status];

              return (
                <Tag
                  color={colorSet.color}
                  textColor={colorSet.textColor}
                  style={{borderRadius: 6, maxWidth: 100}}
                  text={text}
                />
              );
            },
            width: 70,
          },
          {
            title: '委託單狀態',
            render: (order) => {
              return (
                <Tag
                  textColor={
                    order.period_order?.status === 'extra_payment_waiting'
                      ? '#9b9b9b'
                      : '#fff'
                  }
                  color={
                    order.period_order?.status === 'extra_payment_waiting'
                      ? '#e6e6e6'
                      : appConfig.colors.main
                  }
                  style={{borderRadius: 6}}
                  text={PERIOD_STATUS[order.period_order?.status]}
                />
              );
            },
            width: 70,
          },
          {
            title: '運送方式',
            render: (order) => <LogisticCell order={order} />,
            width: 100,
          },
          {
            title: '期數',
            key: 'times',
            width: 70,
            render: (record) => {
              const detail = JSON.parse(record.payment_transaction_detail);
              if (detail.Result) {
                return (
                  (detail.Result.AlreadyTimes || 1) +
                  '/' +
                  (detail.Result.TotalTimes || detail.Result.AuthTimes)
                );
              } // first tranx is different
              return null;
            },
          },
          {
            title: '扣款日',
            key: 'auth_date',
            width: 70,
            render: (record) => {
              const detail = JSON.parse(record.payment_transaction_detail);

              if (detail?.Result?.AuthDate) {
                return detail.Result.AuthDate.slice(0, 10);
              }

              if (detail?.Result?.DateArray) {
                return detail?.Result?.DateArray.split(',')[0]; // first tranx is different
              }
              return null;
            },
          },
          {
            title: '總價',
            render: (order) => (
              <span style={{color: appConfig.colors.main}}>{order.amount}</span>
            ),
            width: 70,
          },
        ];
        break;

      default:
        columns = [
          {
            title: '訂單編號',
            render: (instance) => (
              <Link to={`/order?id=${instance.id}`}>
                <Text size="sm" color={appConfig.colors.main}>
                  {instance.display_id || `-`}
                </Text>
              </Link>
            ),
            width: 70,
            fixed: 'left',
            expandable: true,
          },
          {
            title: '訂單狀態',
            render: (order) => <OrderStateCell order={order} />,
            width: 100,
          },
          {
            title: '付款狀態',
            render: (order) => {
              let colorSet = PAYMENT_STATUS_COLOR[order.payment_status] || {};
              let text = PAYMENT_STATUS[order.payment_status];

              return (
                <Tag
                  color={colorSet.color}
                  textColor={colorSet.textColor}
                  style={{borderRadius: 6, maxWidth: 100}}
                  text={text}
                />
              );
            },
            width: 70,
          },
          {
            title: '運送方式',
            render: (order) => <LogisticCell order={order} />,
            width: 100,
          },
          {
            title: '總價',
            render: (order) => (
              <span style={{color: appConfig.colors.main}}>{order.amount}</span>
            ),
            width: 70,
          },
          {
            title: '訂單類型',
            render: (order) => {
              return ORDER_TYPE[order.order_type];
            },
            width: 70,
          },
        ];
        break;
    }
    return columns;
  }, [filters.type]);

  return {
    columns,
    orders: records,
    total,
    filters,
    changeFilters,
  };
}
